import {
    AfterContentInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChildren,
    ElementRef,
    EventEmitter,
    forwardRef,
    Input,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import {NG_VALUE_ACCESSOR} from '@angular/forms';
import {queryListToObservable} from 'app/core/helpers/observable';
import {includesQuery} from 'app/core/helpers/string';
import {trackByProperty} from 'app/core/helpers/trackby';
import {DomService} from 'app/core/services/dom.service';
import {ListOptionComponent} from 'app/shared/components/drop-down-list/list-option/list-option.component';
import {fromEvent, Observable, Subject, Subscription} from 'rxjs';
import {ChangeEventData, CustomFormComponent} from '../../classes/custom-form-component';
import {DropDownComponent, ExpandingMode} from '../drop-down/drop-down.component';
import {SearchInputComponent} from '../search-input/search-input.component';
import {isEqualDeep} from 'app/core/helpers/object';
import {debounce} from 'app/core/helpers/decorators';

export interface DropDownOption {
    icon?: string;
    svgImage?: string;
    label?: string;
    value: any;
    disabledOption?: boolean;
}

@Component({
    selector: 'drop-down-list',
    templateUrl: './drop-down-list.component.html',
    styleUrls: ['./drop-down-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DropDownListComponent),
            multi: true,
        },
    ],
})
export class DropDownListComponent extends CustomFormComponent<DropDownOption> implements OnInit, OnDestroy, AfterContentInit {
    static EMPTY_PLACEHOLDER = 'Select…';
    static NO_RESULTS_PLACEHOLDER = 'No results';

    @Input() modern: boolean;
    @Input() search: boolean;
    @Input() searchPlaceholder: string;
    @Input() empty: boolean;
    @Input() emptyPlaceholder: string;
    @Input() noResultsPlaceholder: string;
    @Input() expandingMode: ExpandingMode;
    @Output() valueChanges = new EventEmitter<ChangeEventData<any>>();
    @Output() searchChanges = new EventEmitter<ChangeEventData<any>>();
    @ViewChild('dropDown', {static: false}) dropDown: DropDownComponent;
    @ViewChild('searchInput', {static: false}) searchInput: SearchInputComponent;
    @ViewChild('optionsWrapper', {static: false}) optionsWrapper: ElementRef<HTMLDivElement>;
    @ContentChildren(ListOptionComponent, {descendants: true}) listOptions: QueryList<ListOptionComponent>;
    @Input() optionsListTemplate: TemplateRef<any>;
    options$: Observable<Array<DropDownOption>>;
    optionsSubject$ = new Subject<Array<DropDownOption>>();

    lastOptionsValue: Array<DropDownOption>;

    useIcons = false;
    useSvgImage = false;
    searchQuery: string;
    private writeCallsCount = 0;
    private optionsSubscription = Subscription.EMPTY;
    private currentlySelected: DropDownOption;
    private readonly emptyObject: DropDownOption;
    private destroyed = false;
    private hoveredElement: HTMLDivElement;
    private fillEmptySubscription = Subscription.EMPTY;
    private subscriptions = new Map<string, Subscription>();

    trackByValue = trackByProperty<DropDownOption>('value');

    private safeDetectChanges() {
        if (this.destroyed) {
            return;
        }
        this.cd.detectChanges();
    }

    constructor(
        private domService: DomService,
        cd: ChangeDetectorRef,
        $el: ElementRef<HTMLElement>
    ) {
        super($el, cd);
        this.emptyObject = {
            value: null,
        };
        Object.defineProperty(this.emptyObject, 'label', {
            enumerable: true,
            configurable: true,
            get: () => this.emptyPlaceholder || '',
            set: () => {},
        });
    }

    ngOnInit() {
        this.initInputValue('empty', false);
        this.initInputValue('emptyPlaceholder', DropDownListComponent.EMPTY_PLACEHOLDER);
        this.initInputValue('search', false);
        this.initInputValue('searchPlaceholder', SearchInputComponent.PLACEHOLDER);
        this.initInputValue('noResultsPlaceholder', DropDownListComponent.NO_RESULTS_PLACEHOLDER);
    }

    ngAfterContentInit() {
        this.options$ = queryListToObservable(this.listOptions);
        this.optionsSubscription = this.options$.subscribe(options => {
            this.lastOptionsValue = options;
            this.optionsSubject$.next(options);
            this.updateOptions(options);
            // if (!options.find(opt => opt.value === this.currentlySelected)) {
            this.currentlySelected = null;
            // }
            this.cd.markForCheck();
        });
    }

    ngOnDestroy() {
        this.destroyed = true;
        this.optionsSubscription.unsubscribe();
        this.fillEmptySubscription.unsubscribe();
        for (let subscription of this.subscriptions.values()) {
            subscription.unsubscribe();
        }
        this.subscriptions.clear();
    }

    private updateOptions(options: Array<DropDownOption>) {
        this.useIcons = options.some(option => !!option.icon);
        this.useSvgImage = options.some(option => !!option.svgImage);
    }

    private getSelectedOption(value = this.internalValue) {
        if (Array.isArray(this.lastOptionsValue) && this.lastOptionsValue.length) {
            let listedOption = this.lastOptionsValue.find(option => isEqualDeep(option.value, value));
            if (listedOption) {
                return listedOption;
            }
        }
        return this.emptyObject;
    }

    writeValue(value) {
        let write = () => {
            if (this.valueDefined(value)) {
                this.currentlySelected = null;
                super.writeValue(value);
            } else if (!this.empty) {
                this.invokeChangeCallback(this.getOptionValue(this.lastOptionsValue[0]));
            }
            this.safeDetectChanges();
        };
        if (this.writeCallsCount) {
            write();
        } else {
            if (this.valueDefined(value) && value !== null) {
                write();
            } else {
                this.writeCallsCount++;
            }
        }
    }

    getSelected() {
        if (!this.currentlySelected) {
            this.currentlySelected = this.getSelectedOption(this.internalValue) || this.emptyObject;
        }
        return this.currentlySelected;
    }

    isSelected(option: DropDownOption) {
        return isEqualDeep(option.value, this.internalValue);
    }

    isDisabled(option: DropDownOption) {
        return option.disabledOption;
    }

    onToggleOptions(state: boolean) {
        this.safeDetectChanges();
        this.searchQuery = null;
        this.focusSearch(state);
        if (state) {
            this.subscriptions.set(
                'keydown',
                fromEvent(document, 'keydown').subscribe((event: KeyboardEvent) => this.onKeyDown(event))
            );
        } else {
            if (this.subscriptions.has('keydown')) {
                this.subscriptions.get('keydown').unsubscribe();
            }
            this.dismissHighlightedOption();
        }
    }

    onOptionClick(option: DropDownOption) {
        this.currentlySelected = null;
        this.invokeChangeCallback(this.getOptionValue(option));
        this.dropDown.toggleContent(false);
    }

    @debounce(300)
    onSearch(query: string) {
        this.searchQuery = query;
        this.refreshHeight();
        this.searchChanges.emit({oldValue: this.searchQuery, newValue: query});
    }

    refreshHeight() {
        this.dropDown.updateHeight();
    }

    onKeyDown(event: KeyboardEvent) {
        if (/ArrowUp|ArrowDown|Enter|Escape/.test(event.key)) {
            event.preventDefault();
        }
        switch (event.key) {
            case 'ArrowUp':
            case 'ArrowDown':
                return this.hoverOption(event.key);
            case 'Enter':
            case ' ':
                return this.selectOption();
            case 'Escape':
                return this.dropDown.toggleContent(false);
        }
    }

    filterOptions(options: Array<DropDownOption>): Array<DropDownOption> {
        if (this.searchQuery) {
            let queryTester = includesQuery(this.searchQuery);
            return options.filter(({value, label}) => queryTester.test([label, String(value)]));
        }
        return options;
    }

    private focusSearch(focusState: boolean) {
        if (this.searchInput) {
            if (focusState) {
                this.searchInput.getInput().focus();
            } else {
                this.searchInput.getInput().blur();
            }
        }
    }

    private getOptionValue(option: DropDownOption) {
        return option && option.value;
    }

    private hoverOption(key: string) {
        let options = [...this.optionsWrapper.nativeElement.children] as Array<HTMLDivElement>;
        let currentElement = options.find(el => el.matches('.hover'));
        let elementToHover;
        if (key === 'ArrowDown') {
            elementToHover = (currentElement && this.getNextOptionElement(currentElement)) || options[0];
        }
        if (key === 'ArrowUp') {
            elementToHover = (currentElement && this.getPreviousOptionElement(currentElement)) || options[options.length - 1];
        }
        if (currentElement) {
            currentElement.classList.remove('hover');
        }
        this.setHighlightedOption(elementToHover);
    }

    private getNextOptionElement(el: HTMLDivElement): HTMLDivElement {
        let next = el;
        do {
            next = next.nextElementSibling as HTMLDivElement;
        } while (next && !next.matches('.drop-down-option'));
        return next;
    }

    private getPreviousOptionElement(el: HTMLDivElement): HTMLDivElement {
        let previous = el;
        do {
            previous = previous.previousElementSibling as HTMLDivElement;
        } while (previous && !previous.matches('.drop-down-option'));
        return previous;
    }

    private setHighlightedOption(el: HTMLDivElement) {
        if (el) {
            this.hoveredElement = el;
            el.classList.add('hover');
            el.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            });
        }
    }

    private dismissHighlightedOption() {
        if (this.hoveredElement) {
            this.hoveredElement.classList.remove('hover');
        }
        this.hoveredElement = null;
    }

    private selectOption() {
        if (this.hoveredElement) {
            this.hoveredElement.dispatchEvent(
                new MouseEvent('click', {
                    bubbles: true,
                })
            );
        }
    }

    private valueDefined(val: any): boolean {
        return val !== undefined && val !== '';
    }
}
